if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    // Lazily resolve the cart element - fixes timing issue where
    // cart-drawer isn't in the DOM when product-form constructs
    getCart() {
      if (!this._cart) {
        this._cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }
      return this._cart;
    }

    handleFinally() {
      this.submitButton.classList.remove('loading');
      const cart = this.getCart();
      if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      this.querySelector('.loading-overlay__spinner').classList.add('hidden');
    }

    handleResponse(response) {
      const cart = this.getCart();
      if (response.status) {
        this.handleErrorMessage(response.description);

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.querySelector('span').classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
        return;
      } else if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      this.error = false;
      const quickAddModal = this.closest('quick-add-modal');
      if (quickAddModal) {
        document.body.addEventListener('modalClosed', () => {
          setTimeout(() => { cart.renderContents(response) });
        }, { once: true });
        quickAddModal.hide(true);
      } else {
        cart.renderContents(response);
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      const cart = this.getCart();
      
      const formData = new FormData(this.form);
      const formId = this.form.getAttribute('id');
      if (formId) {
        const extraElements = document.querySelectorAll(`[form="${formId}"]`);
        extraElements.forEach(el => {
          if (!el.name) return;
          if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
          if (el.value === '') return;
          formData.set(el.name, el.value);
        });
      }
      const toppingId = this.form.getAttribute('data-topping-id');
      
      let extraUnits = 0;
      let addonNames = [];

      // Search for selected addons
      // They can be inside the form (Order Menu) or outside linked by form ID (PDP)
      const addonPills = this.form.querySelectorAll('.addon-pill.selected');
      const selectionPillsInside = this.form.querySelectorAll('.selection-pill__input:checked');
      let allAddons = [...addonPills, ...selectionPillsInside];

      if (this.form.getAttribute('id')) {
        const selectionPillsOutside = document.querySelectorAll(`.selection-pill__input[form="${this.form.getAttribute('id')}"]:checked`);
        allAddons = [...allAddons, ...selectionPillsOutside];
      }

      const uniqueAddons = [...new Set(allAddons)];

      uniqueAddons.forEach(element => {
        const price = parseFloat(element.dataset.price || 0);
        if (price > 0) {
          extraUnits += Math.round(price / 100);
        }
        
        let name = '';
        if (element.classList.contains('addon-pill')) {
           name = element.dataset.name;
        } else {
           // For checkboxes, extract the real name from the input's name attribute
           // e.g. "properties[Fruit - Banana]" -> "Fruit - Banana"
           const inputName = element.getAttribute('name') || '';
           const match = inputName.match(/properties\[(.+)\]/);
           name = match ? match[1] : element.value;
        }
        if (name) addonNames.push(name);
      });

      if (addonNames.length > 0) {
        formData.set('properties[Add-ons]', addonNames.join(', '));
      }

      if (extraUnits > 0 && toppingId) {
        config.headers['Content-Type'] = 'application/json';
        const mainProductId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || 1);
        
        const properties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propKey = key.replace('properties[', '').replace(']', '');
            properties[propKey] = value;
          }
        }

        const items = [
          { id: parseInt(mainProductId), quantity: quantity, properties: properties },
          { id: parseInt(toppingId), quantity: extraUnits * quantity }
        ];

        let sectionsArr = [];
        if (cart) {
          sectionsArr = cart.getSectionsToRender().map((section) => section.id);
          cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify({
          items: items,
          sections: sectionsArr,
          sections_url: window.location.pathname
        });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // Multi-item response has 'items' array; cart drawer needs 'sections'
            if (response.items && response.sections) {
              this.handleResponse(response);
            } else if (response.items) {
              // Sections not returned - re-fetch them for the cart drawer
              if (cart) {
                const sectionIds = cart.getSectionsToRender().map((s) => s.id);
                fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)
                  .then(r => r.json())
                  .then(sections => {
                    response.sections = sections;
                    this.handleResponse(response);
                  })
                  .catch(() => window.location.reload());
              } else {
                window.location = window.routes.cart_url;
              }
            } else {
              // Error response
              this.handleResponse(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.handleFinally();
          });
      } else {
        delete config.headers['Content-Type'];
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleResponse(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.handleFinally();
          });
      }
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
